import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Home, User, ArrowLeftRight, Building2, Check, UtensilsCrossed, Truck, Car, Wrench, FileText, Plus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const RoleSwitcher = ({ currentPortal = null }) => {
  const navigate = useNavigate();
  const { user, activeMode, switchMode, hasHostRole, hasGuestRole } = useAuth();

  const hasRole = (role) => user?.roles?.includes(role);
  const hasDeliveryRiderRole = () => hasRole('delivery_rider');
  const hasRestaurantPartnerRole = () => hasRole('restaurant_partner');
  const hasDriverRole = () => hasRole('driver');
  const hasProviderRole = () => hasRole('service_provider');
  const hasLandlordRole = () => hasRole('landlord');
  const canSwitchToLandlord = hasLandlordRole();

  // Don't render if no user is logged in
  if (!user) return null;

  const handleModeSwitch = (mode) => {
    const success = switchMode(mode);
    if (success) {
      if (mode === 'host') {
        navigate('/host/dashboard', { replace: true });
      } else {
        navigate('/guest/browse', { replace: true });
      }
    }
  };

  // Determine the active display based on current portal
  const effectivePortal = currentPortal || (activeMode === 'host' ? 'host' : 'guest');

  // Check if user can switch modes — all users can be guests
  const canSwitchToHost = hasHostRole();
  const canSwitchToGuest = true; // Everyone can be a guest

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="flex items-center gap-2"
          data-testid="role-switcher-btn"
        >
          {effectivePortal === 'host' ? (
            <>
              <Building2 size={16} />
              <span className="hidden sm:inline">Host Mode</span>
            </>
          ) : effectivePortal === 'provider' ? (
            <>
              <Wrench size={16} />
              <span className="hidden sm:inline">Provider Mode</span>
            </>
          ) : effectivePortal === 'landlord' ? (
            <>
              <FileText size={16} />
              <span className="hidden sm:inline">Landlord Mode</span>
            </>
          ) : (
            <>
              <User size={16} />
              <span className="hidden sm:inline">Guest Mode</span>
            </>
          )}
          <ArrowLeftRight size={14} className="ml-1 text-gray-400" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex items-center gap-2">
          <span className="truncate max-w-[180px]">{user.name || user.email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {/* Host Mode - only show if user has host role */}
        {canSwitchToHost && (
          <DropdownMenuItem
            onClick={() => handleModeSwitch('host')}
            className={effectivePortal === 'host' ? 'bg-[#1F3A68]/10' : ''}
            data-testid="switch-to-host"
          >
            <Building2 size={16} className="mr-2" />
            <span>Host Mode</span>
            {effectivePortal === 'host' && (
              <Check className="ml-auto h-4 w-4 text-[#1F3A68]" />
            )}
          </DropdownMenuItem>
        )}

        {/* Landlord Mode - only show if user has landlord role */}
        {canSwitchToLandlord && (
          <DropdownMenuItem
            onClick={() => navigate('/landlord/dashboard', { replace: true })}
            className={effectivePortal === 'landlord' ? 'bg-[#1F3A68]/10' : ''}
            data-testid="switch-to-landlord"
          >
            <FileText size={16} className="mr-2" />
            <span>Landlord Mode</span>
            {effectivePortal === 'landlord' && (
              <Check className="ml-auto h-4 w-4 text-[#1F3A68]" />
            )}
          </DropdownMenuItem>
        )}

        {/* Guest Mode - always available */}
        {canSwitchToGuest && (
          <DropdownMenuItem
            onClick={() => {
              handleModeSwitch('guest');
              navigate('/guest/browse', { replace: true });
            }}
            className={effectivePortal === 'guest' ? 'bg-[#1F3A68]/10' : ''}
            data-testid="switch-to-guest"
          >
            <User size={16} className="mr-2" />
            <span>Guest Mode</span>
            {effectivePortal === 'guest' && (
              <Check className="ml-auto h-4 w-4 text-[#1F3A68]" />
            )}
          </DropdownMenuItem>
        )}

        {/* Service Provider Mode */}
        {hasProviderRole() && effectivePortal !== 'provider' && (
          <DropdownMenuItem onClick={() => navigate('/provider/dashboard')} data-testid="switch-to-provider">
            <Wrench size={16} className="mr-2" />
            <span>Provider Dashboard</span>
          </DropdownMenuItem>
        )}

        {/* Driver Mode */}
        {hasDriverRole() && (
          <DropdownMenuItem onClick={() => navigate('/driver/dashboard')} data-testid="switch-to-driver">
            <Car size={16} className="mr-2" />
            <span>Driver Dashboard</span>
          </DropdownMenuItem>
        )}

        {/* Delivery Rider Mode */}
        {hasDeliveryRiderRole() && (
          <DropdownMenuItem onClick={() => navigate('/delivery/dashboard')} data-testid="switch-to-delivery-rider">
            <Truck size={16} className="mr-2" />
            <span>Delivery Dashboard</span>
          </DropdownMenuItem>
        )}

        {/* Restaurant Partner Mode */}
        {hasRestaurantPartnerRole() && (
          <DropdownMenuItem onClick={() => navigate('/restaurant/dashboard')} data-testid="switch-to-restaurant">
            <UtensilsCrossed size={16} className="mr-2" />
            <span>Restaurant Dashboard</span>
          </DropdownMenuItem>
        )}

        {/* Add another role — single entry that leads to a page showing
            every role the user does not yet have. Replaces the old
            "Become a Host" item which only surfaced one of six options. */}
        {(() => {
          const ALL_PARTNER_ROLES = ['host', 'landlord', 'driver', 'delivery_rider', 'restaurant_partner', 'service_provider'];
          const missing = ALL_PARTNER_ROLES.filter(r => !hasRole(r));
          if (missing.length === 0) return null;
          return (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigate('/add-role')}
                className="text-[#1F3A68] focus:text-[#1F3A68] font-medium"
                data-testid="add-role-btn"
              >
                <Plus size={16} className="mr-2" />
                <span>Add another role</span>
              </DropdownMenuItem>
            </>
          );
        })()}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default RoleSwitcher;
